import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Truck, Package, MapPin, Clock, ArrowLeft, Plus, Search
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const LogisticsMyShipments = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchShipments();
  }, [filter]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api.get(`/logistics/my-shipments${params}`);
      setShipments(response.data.shipments || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load shipments',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'accepted': return 'bg-blue-100 text-blue-700';
      case 'pickup_en_route': return 'bg-blue-100 text-blue-700';
      case 'goods_collected': return 'bg-purple-100 text-purple-700';
      case 'in_transit': return 'bg-orange-100 text-orange-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'accepted': return 'Accepted';
      case 'pickup_en_route': return 'En Route';
      case 'goods_collected': return 'Collected';
      case 'in_transit': return 'In Transit';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/logistics')}
            className="flex items-center gap-2 text-orange-100 hover:text-white mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">My Shipments</h1>
            <Button 
              onClick={() => navigate('/logistics')}
              variant="secondary"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Booking
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'in_transit', label: 'In Transit' },
            { key: 'delivered', label: 'Delivered' },
            { key: 'cancelled', label: 'Cancelled' }
          ].map((f) => (
            <Button
              key={f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.key)}
              className={filter === f.key ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto" />
            <p className="text-gray-500 mt-2">Loading shipments...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && shipments.length === 0 && (
          <Card className="p-8 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No shipments found</p>
            <Button 
              onClick={() => navigate('/logistics')}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Book Your First Shipment
            </Button>
          </Card>
        )}

        {/* Shipments List */}
        {!loading && shipments.length > 0 && (
          <div className="space-y-3">
            {shipments.map((shipment) => (
              <Card 
                key={shipment.id}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate('/logistics/track', { 
                  state: { trackingCode: shipment.trackingCode } 
                })}
                data-testid={`shipment-${shipment.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono font-bold text-orange-600">
                      {shipment.trackingCode}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(shipment.createdAt)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    getStatusColor(shipment.status)
                  }`}>
                    {getStatusLabel(shipment.status)}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                    <p className="text-gray-600 line-clamp-1">{shipment.pickupAddress}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                    <p className="text-gray-600 line-clamp-1">{shipment.dropoffAddress}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Package className="h-4 w-4" />
                    <span>{shipment.cargo?.description}</span>
                  </div>
                  <p className="font-semibold text-orange-600">
                    K{shipment.price?.toFixed(2)}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Track Shipment */}
        <Card className="p-4 bg-orange-50 border-orange-200">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-orange-500" />
            <div className="flex-1">
              <p className="font-medium">Have a tracking code?</p>
              <p className="text-sm text-gray-500">Track any shipment by code</p>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate('/logistics/track')}
            >
              Track
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LogisticsMyShipments;
